import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { SagaIterator } from 'redux-saga';
import lessonsService, {
    LessonFilters,
    PaginationParams,
    ExerciseSubmission,
    LessonCompletion,
} from '@/services/lessonsService';
import {
    fetchLessonsRequest,
    fetchLessonsSuccess,
    fetchLessonsFailure,
    fetchLessonByIdRequest,
    fetchLessonByIdSuccess,
    fetchLessonByIdFailure,
    fetchLessonProgressRequest,
    fetchLessonProgressSuccess,
    fetchLessonProgressFailure,
    submitExerciseRequest,
    submitExerciseSuccess,
    submitExerciseFailure,
    completeLessonRequest,
    completeLessonSuccess,
    completeLessonFailure,
    fetchLessonStatsRequest,
    fetchLessonStatsSuccess,
    fetchLessonStatsFailure,
    fetchRecommendedLessonsRequest,
    fetchRecommendedLessonsSuccess,
    fetchRecommendedLessonsFailure,
    fetchBookmarkedLessonsRequest,
    fetchBookmarkedLessonsSuccess,
    fetchBookmarkedLessonsFailure,
    fetchLessonCategoriesRequest,
    fetchLessonCategoriesSuccess,
    fetchLessonCategoriesFailure,
    bookmarkLessonRequest,
    bookmarkLessonSuccess,
    bookmarkLessonFailure,
} from '../slices/lessonsSlice';

// Worker Sagas
function* fetchLessonsWorker(
    action: PayloadAction<{ filters?: LessonFilters; pagination?: PaginationParams }>,
): SagaIterator {
    try {
        const { filters, pagination } = action.payload;
        const response = yield call(lessonsService.getLessons, filters, pagination);
        yield put(fetchLessonsSuccess(response));
    } catch (error: any) {
        yield put(fetchLessonsFailure(error.response?.data?.message || 'Failed to fetch lessons'));
    }
}

function* fetchLessonByIdWorker(action: PayloadAction<string>): SagaIterator {
    try {
        const lesson = yield call(lessonsService.getLessonById, action.payload);
        yield put(fetchLessonByIdSuccess(lesson));
    } catch (error: any) {
        yield put(fetchLessonByIdFailure(error.response?.data?.message || 'Failed to fetch lesson'));
    }
}

function* fetchLessonProgressWorker(action: PayloadAction<string | undefined>): SagaIterator {
    try {
        const progress = yield call(lessonsService.getLessonProgress, action.payload);
        yield put(fetchLessonProgressSuccess(progress));
    } catch (error: any) {
        yield put(fetchLessonProgressFailure(error.response?.data?.message || 'Failed to fetch lesson progress'));
    }
}

function* submitExerciseWorker(action: PayloadAction<ExerciseSubmission>): SagaIterator {
    try {
        const result = yield call(lessonsService.submitExercise, action.payload);
        yield put(submitExerciseSuccess(result));
    } catch (error: any) {
        yield put(submitExerciseFailure(error.response?.data?.message || 'Failed to submit exercise'));
    }
}

function* completeLessonWorker(action: PayloadAction<LessonCompletion>): SagaIterator {
    try {
        const progress = yield call(lessonsService.completeLesson, action.payload);
        yield put(completeLessonSuccess(progress));
    } catch (error: any) {
        yield put(completeLessonFailure(error.response?.data?.message || 'Failed to complete lesson'));
    }
}

function* fetchLessonStatsWorker(): SagaIterator {
    try {
        const stats = yield call(lessonsService.getLessonStats);
        yield put(fetchLessonStatsSuccess(stats));
    } catch (error: any) {
        yield put(fetchLessonStatsFailure(error.response?.data?.message || 'Failed to fetch lesson stats'));
    }
}

function* fetchRecommendedLessonsWorker(): SagaIterator {
    try {
        const lessons = yield call(lessonsService.getRecommendedLessons);
        yield put(fetchRecommendedLessonsSuccess(lessons));
    } catch (error: any) {
        yield put(
            fetchRecommendedLessonsFailure(error.response?.data?.message || 'Failed to fetch recommended lessons'),
        );
    }
}

function* fetchBookmarkedLessonsWorker(): SagaIterator {
    try {
        const lessons = yield call(lessonsService.getBookmarkedLessons);
        yield put(fetchBookmarkedLessonsSuccess(lessons));
    } catch (error: any) {
        yield put(fetchBookmarkedLessonsFailure(error.response?.data?.message || 'Failed to fetch bookmarked lessons'));
    }
}

function* fetchLessonCategoriesWorker(): SagaIterator {
    try {
        const categories = yield call(lessonsService.getLessonCategories);
        yield put(fetchLessonCategoriesSuccess(categories));
    } catch (error: any) {
        yield put(fetchLessonCategoriesFailure(error.response?.data?.message || 'Failed to fetch lesson categories'));
    }
}

function* bookmarkLessonWorker(action: PayloadAction<string>): SagaIterator {
    try {
        yield call(lessonsService.bookmarkLesson, action.payload);
        yield put(bookmarkLessonSuccess(action.payload));
    } catch (error: any) {
        yield put(bookmarkLessonFailure(error.response?.data?.message || 'Failed to bookmark lesson'));
    }
}

// Watcher Sagas
function* watchLessons(): SagaIterator {
    yield takeLatest(fetchLessonsRequest.type, fetchLessonsWorker);
    yield takeLatest(fetchLessonByIdRequest.type, fetchLessonByIdWorker);
    yield takeLatest(fetchLessonProgressRequest.type, fetchLessonProgressWorker);
    yield takeLatest(submitExerciseRequest.type, submitExerciseWorker);
    yield takeLatest(completeLessonRequest.type, completeLessonWorker);
    yield takeLatest(fetchLessonStatsRequest.type, fetchLessonStatsWorker);
    yield takeLatest(fetchRecommendedLessonsRequest.type, fetchRecommendedLessonsWorker);
    yield takeLatest(fetchBookmarkedLessonsRequest.type, fetchBookmarkedLessonsWorker);
    yield takeLatest(fetchLessonCategoriesRequest.type, fetchLessonCategoriesWorker);
    yield takeLatest(bookmarkLessonRequest.type, bookmarkLessonWorker);
}

export default watchLessons;
